import type { Flow360Case, Flow360SurfaceMesh, FullConfigResponse } from "../../flow360/types";
import type { Flow360PanelRunEstimate, PanelTargetEstimate } from "./ThreadV5Flow360Estimate";

export type FilamentBackendTarget = "webgpu" | "cuda";
export type FilamentDecimationStrategy = "smart" | "uniform" | "source";

export interface FilamentInputControl {
  key: string;
  label: string;
  value: number;
  unit?: string;
  pinned: boolean;
  source: "flow360-config" | "default";
}

export interface FilamentModelPlan {
  backend: FilamentBackendTarget;
  backendLabel: string;
  sourceSurfaceMeshId: string | null;
  sourceSurfaceMeshName: string | null;
  sourceCaseId: string | null;
  sourceCaseName: string | null;
  sourcePanels: number | null;
  targetPanels: number | null;
  decimationRatio: number | null;
  decimationStrategy: FilamentDecimationStrategy;
  meshStrategyLabel: string;
  meshStrategyDetail: string;
  canUseSourceMesh: boolean;
  expectedDenseMatrixGb: number | null;
  relativeWork: number | null;
  wakeIterations: number;
  inputControls: FilamentInputControl[];
}

export interface CreateFilamentModelPlanInput {
  estimate: Flow360PanelRunEstimate;
  backend: FilamentBackendTarget;
  surfaceMesh?: Flow360SurfaceMesh | null;
  case?: Flow360Case | null;
  targetPanels?: number | null;
  decimationStrategy?: FilamentDecimationStrategy;
  wakeIterations?: number;
  inputControls?: FilamentInputControl[];
}

type ScalarValue = { value: number; unit?: string };

const DEFAULT_INPUTS: FilamentInputControl[] = [
  { key: "alpha_deg", label: "Alpha", value: 2, unit: "deg", pinned: false, source: "default" },
  { key: "beta_deg", label: "Beta", value: 0, unit: "deg", pinned: true, source: "default" },
  { key: "mach", label: "Mach", value: 0.2, pinned: true, source: "default" },
  { key: "reynolds", label: "Reynolds", value: 1_000_000, pinned: true, source: "default" },
];

const INPUT_PATTERNS: Array<{
  key: string;
  label: string;
  unit?: string;
  pattern: RegExp;
}> = [
  { key: "alpha_deg", label: "Alpha", unit: "deg", pattern: /(^|[._-])(alpha|angleofattack|angle_of_attack|aoa)([._-]|$)/i },
  { key: "beta_deg", label: "Beta", unit: "deg", pattern: /(^|[._-])(beta|sideslip|angleofsideslip|angle_of_sideslip)([._-]|$)/i },
  { key: "mach", label: "Mach", pattern: /(^|[._-])mach(number)?([._-]|$)/i },
  { key: "reynolds", label: "Reynolds", pattern: /(^|[._-])(reynolds|reynoldsnumber|re)([._-]|$)/i },
];

function scalarValue(value: unknown): ScalarValue | null {
  if (typeof value === "number" && Number.isFinite(value)) return { value };
  if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) {
    return { value: Number(value) };
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const direct = scalarValue(record.value ?? record.Value ?? record.magnitude);
  if (!direct) return null;
  const unit = record.units ?? record.unit ?? record.Unit;
  return {
    value: direct.value,
    unit: typeof unit === "string" && unit ? unit : direct.unit,
  };
}

function findScalar(source: unknown, pattern: RegExp, path = "", depth = 0): ScalarValue | null {
  if (source == null || depth > 8) return null;
  const direct = scalarValue(source);
  if (direct && pattern.test(path)) return direct;
  if (Array.isArray(source)) {
    for (const item of source.slice(0, 96)) {
      const nested = findScalar(item, pattern, path, depth + 1);
      if (nested) return nested;
    }
    return null;
  }
  if (typeof source !== "object") return null;
  for (const [key, value] of Object.entries(source as Record<string, unknown>)) {
    const nextPath = path ? `${path}.${key}` : key;
    const candidate = scalarValue(value);
    if (candidate && pattern.test(nextPath)) return candidate;
    const nested = findScalar(value, pattern, nextPath, depth + 1);
    if (nested) return nested;
  }
  return null;
}

function denseMatrixGb(panelCount: number | null | undefined): number | null {
  if (!panelCount || panelCount <= 0) return null;
  return (panelCount * panelCount * 8) / 1_073_741_824;
}

function backendTarget(estimate: Flow360PanelRunEstimate, backend: FilamentBackendTarget): PanelTargetEstimate {
  return backend === "cuda" ? estimate.cuda : estimate.mac;
}

function backendLabel(backend: FilamentBackendTarget): string {
  return backend === "cuda" ? "CUDA dev GPU" : "WebGPU / local";
}

function meshStrategyLabel(strategy: FilamentDecimationStrategy, canUseSourceMesh: boolean): string {
  if (canUseSourceMesh) return "Use Flow360 surface mesh directly";
  if (strategy === "source") return "Source mesh requested, but too large";
  if (strategy === "uniform") return "Uniform panel reduction";
  return "Smart surface downsampling";
}

function meshStrategyDetail(strategy: FilamentDecimationStrategy, sourcePanels: number | null, targetPanels: number | null): string {
  if (!sourcePanels || !targetPanels) {
    return "Resolve a case or select a surface mesh so Thread can turn Flow360 metadata into a Filament panelization target.";
  }
  if (sourcePanels <= targetPanels * 1.1) {
    return "The source surface mesh is already close to the target panel count, so Filament can start from it without destructive simplification.";
  }
  if (strategy === "source") {
    return "Running the full Flow360 surface mesh as a dense panel model is likely wasteful; switch to smart downsampling unless this is a tiny validation case.";
  }
  if (strategy === "uniform") {
    return "Uniform decimation is useful for solver smoke tests, but it may erase control edges, high-curvature leading edges, and wake-relevant seams.";
  }
  return "Preserve named patches, component boundaries, sharp curvature, lifting surfaces, and wake attachment seams while reducing broad low-curvature areas first.";
}

export function extractFilamentInputs(fullConfig: FullConfigResponse["config"] | null | undefined): FilamentInputControl[] {
  return DEFAULT_INPUTS.map((input) => {
    const matcher = INPUT_PATTERNS.find((item) => item.key === input.key);
    const found = matcher ? findScalar(fullConfig, matcher.pattern) : null;
    if (!found) return { ...input };
    return {
      ...input,
      value: found.value,
      unit: found.unit ?? matcher?.unit ?? input.unit,
      source: "flow360-config",
    };
  });
}

export function createFilamentModelPlan(input: CreateFilamentModelPlanInput): FilamentModelPlan {
  const target = backendTarget(input.estimate, input.backend);
  const targetPanels = input.targetPanels && input.targetPanels > 0 ? input.targetPanels : target.targetPanels;
  const sourcePanels = input.estimate.sourcePanels;
  const canUseSourceMesh = Boolean(sourcePanels && targetPanels && sourcePanels <= targetPanels * 1.1);
  const strategy = input.decimationStrategy ?? "smart";

  return {
    backend: input.backend,
    backendLabel: backendLabel(input.backend),
    sourceSurfaceMeshId: input.surfaceMesh?.id ?? null,
    sourceSurfaceMeshName: input.surfaceMesh?.name ?? null,
    sourceCaseId: input.case?.id ?? null,
    sourceCaseName: input.case?.name ?? null,
    sourcePanels,
    targetPanels,
    decimationRatio: sourcePanels && targetPanels ? sourcePanels / targetPanels : null,
    decimationStrategy: strategy,
    meshStrategyLabel: meshStrategyLabel(strategy, canUseSourceMesh),
    meshStrategyDetail: meshStrategyDetail(strategy, sourcePanels, targetPanels),
    canUseSourceMesh,
    expectedDenseMatrixGb: denseMatrixGb(targetPanels),
    relativeWork: target.relativeWork,
    wakeIterations: input.wakeIterations ?? input.estimate.wakeIterations,
    inputControls: input.inputControls ?? extractFilamentInputs(null),
  };
}
